import { CalendarDates } from "@/custom-types";
import { format, parse } from "date-fns";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const dateFormat = "yyyy-MM-dd";

type ParamType = "string" | "number" | "boolean" | "date" | "enum";

interface ParamConfig<T> {
  name: string;
  type: ParamType;
  validValues?: readonly T[];
  defaultValue: T;
}

type ExtractConfigValue<T> = T extends ParamConfig<infer U> ? U : never;

type ParamStateFromConfig<T extends Record<string, ParamConfig<any>>> = {
  [K in keyof T]: ExtractConfigValue<T[K]>;
};

type ParamUpdatesFromConfig<T extends Record<string, ParamConfig<any>>> = Partial<ParamStateFromConfig<T>>;

export function useUrlParams<T extends Record<string, ParamConfig<any>>>(
  paramConfigs: T,
): {
  paramState: ParamStateFromConfig<T>;
  updateParam: <K extends keyof T>(paramKey: K, value: ExtractConfigValue<T[K]>) => void;
  updateParams: (values: ParamUpdatesFromConfig<T>) => void;
  updateDateRange: (dateRange: CalendarDates | undefined, fromKey: keyof T, toKey: keyof T) => void;
  resetParams: () => void;
  hasUrlParams: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const paramState = useMemo(() => {
    return Object.entries(paramConfigs).reduce((acc, [key, config]) => {
      const paramValue = searchParams.get(config.name);
      acc[key] = config.defaultValue;

      if (paramValue === null) {
        return acc;
      }

      switch (config.type) {
        case "number":
          const castedValue = Number(paramValue);
          if (isNaN(castedValue)) {
            console.error(`Invalid number for ${config.name}: ${paramValue}`);
          } else {
            acc[key] = castedValue;
          }
          break;
        case "boolean":
          acc[key] = paramValue === "true";
          break;
        case "date":
          acc[key] = parse(paramValue, dateFormat, new Date());
          break;
        case "enum":
          if (config.validValues && config.validValues.includes(paramValue)) {
            acc[key] = paramValue;
          }
          break;
        case "string":
        default:
          acc[key] = paramValue;
      }

      return acc;
    }, {} as any);
  }, [searchParams, paramConfigs]) as ParamStateFromConfig<T>;

  const updateParam = useCallback(
    <K extends keyof T>(paramKey: K, value: ExtractConfigValue<T[K]>) => {
      const config = paramConfigs[paramKey];

      setSearchParams(params => {
        switch (config.type) {
          case "date":
            if (value) {
              params.set(config.name, format(value as Date, dateFormat));
            } else {
              params.delete(config.name);
            }
            break;
          case "boolean":
          case "number":
          case "enum":
          case "string":
          default:
            if (value === null || value === undefined || value === "") {
              params.delete(config.name);
            } else {
              params.set(config.name, String(value));
            }
        }

        return params;
      });
    },
    [paramConfigs, setSearchParams],
  );

  const updateParams = useCallback(
    (values: ParamUpdatesFromConfig<T>) => {
      setSearchParams(params => {
        Object.entries(values).forEach(([paramKey, value]) => {
          const config = paramConfigs[paramKey];
          if (!config) {
            return;
          }

          switch (config.type) {
            case "date":
              if (value) {
                params.set(config.name, format(value as Date, dateFormat));
              } else {
                params.delete(config.name);
              }
              break;
            case "boolean":
            case "number":
            case "enum":
            case "string":
            default:
              if (value === null || value === undefined || value === "") {
                params.delete(config.name);
              } else {
                params.set(config.name, String(value));
              }
          }
        });

        return params;
      });
    },
    [paramConfigs, setSearchParams],
  );

  const updateDateRange = useCallback(
    (dateRange: CalendarDates | undefined, fromKey: keyof T, toKey: keyof T) => {
      const fromConfig = paramConfigs[fromKey];
      const toConfig = paramConfigs[toKey];

      if (fromConfig.type !== "date" || toConfig.type !== "date") {
        console.error("updateDateRange can only be used with date type parameters");
        return;
      }

      setSearchParams(params => {
        if (dateRange?.from) {
          params.set(fromConfig.name, format(dateRange.from, dateFormat));
        } else {
          params.delete(fromConfig.name);
        }

        if (dateRange?.to) {
          params.set(toConfig.name, format(dateRange.to, dateFormat));
        } else {
          params.delete(toConfig.name);
        }

        return params;
      });
    },
    [paramConfigs, setSearchParams],
  );

  const resetParams = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasUrlParams = searchParams.toString().length > 0;

  return {
    paramState,
    updateParam,
    updateParams,
    updateDateRange,
    resetParams,
    hasUrlParams,
  };
}
